import { CanAccess } from '@modules/common/components/CanAccess';
import { ReasonSuggestions } from '@modules/feedback/components/FeedbackDetails/utils/getReasonSuggestionsFromSuggestedReasons';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { AddRounded } from '@mui/icons-material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Badge,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { Feedback, FeedbackContentType } from 'v2/core/entities/feedback';

interface FeedbackPredictionActionsProps {
  feedback: Feedback;
  reasonSuggestions: ReasonSuggestions[];
  areSuggestionsLoading: boolean;
  isAddPredictionsVisible: boolean;
  shouldShowEditPredictionsButton?: boolean;
  isDetailedView: boolean; // TODO: Remove this after Feedback Redesign goes live
  onOpen: () => void;
  onClose: () => void;
}

export const FeedbackPredictionActions = ({
  feedback,
  reasonSuggestions,
  areSuggestionsLoading,
  isAddPredictionsVisible,
  isDetailedView = false,
  onOpen,
  onClose,
}: FeedbackPredictionActionsProps) => {
  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const isAddingPredictionsDisabled =
    feedback.content.type === FeedbackContentType.Audio;

  if (isAddPredictionsVisible && !isFeedbackRedesignEnabled) {
    return (
      <IconButton color="primary" onClick={onClose} sx={{ p: 0 }}>
        <CloseRoundedIcon />
      </IconButton>
    );
  }

  if (isAddingPredictionsDisabled && !isFeedbackRedesignEnabled) {
    return (
      <Tooltip
        arrow
        placement={isDetailedView ? 'left' : 'right'}
        title={`Adding Predictions is not available for ${feedback.sourceV2?.name}`}
      >
        <AddCircleOutlineRoundedIcon color="disabled" />
      </Tooltip>
    );
  }

  const shouldShowOrangeDot = feedback.hasUnpredictedRCs && !isDetailedView;

  const shouldShowPrimaryBadge =
    !shouldShowOrangeDot &&
    (areSuggestionsLoading || reasonSuggestions.length === 0);

  const renderBadgeContent = () => {
    if (shouldShowOrangeDot || isAddingPredictionsDisabled) {
      return null;
    }

    if (areSuggestionsLoading) {
      return (
        <Stack justifyContent="center" alignItems="center" width="100%">
          <CircularProgress
            sx={{
              width: '100%',
              color: 'background.default',
            }}
            size="small"
          />
        </Stack>
      );
    }

    return reasonSuggestions.length;
  };

  if (isFeedbackRedesignEnabled && isDetailedView) {
    return (
      <CanAccess
        resource={PermissionResource.Prediction}
        operation={PermissionOperation.Write}
        tooltipPlacement="top"
      >
        <Tooltip
          arrow
          placement="right"
          title={
            isAddingPredictionsDisabled
              ? `Adding Predictions is not available for ${feedback.sourceV2?.name}`
              : null
          }
        >
          <Badge
            badgeContent={renderBadgeContent()}
            variant={shouldShowOrangeDot ? 'dot' : 'standard'}
            color={shouldShowPrimaryBadge ? 'primary' : 'warning'}
            sx={{
              '&.MuiBadge-badge': {
                px: '4px !important',
              },
            }}
          >
            <Button
              startIcon={<AddRounded />}
              sx={{
                textTransform: 'none',
              }}
              disabled={isAddingPredictionsDisabled}
              onClick={() => {
                if (isAddPredictionsVisible) {
                  onClose();
                } else {
                  onOpen();
                }
              }}
            >
              Add Predictions
            </Button>
          </Badge>
        </Tooltip>
      </CanAccess>
    );
  }

  return (
    <IconButton color="primary" onClick={onOpen} sx={{ p: 0 }}>
      <Badge
        badgeContent={renderBadgeContent()}
        variant={shouldShowOrangeDot ? 'dot' : 'standard'}
        color={shouldShowPrimaryBadge ? 'primary' : 'warning'}
        sx={{
          '&.MuiBadge-badge': {
            px: '4px !important',
          },
        }}
      >
        <AddCircleOutlineRoundedIcon />
      </Badge>
    </IconButton>
  );
};
